import { useAuth } from "@/hooks/use-auth";
import { useTeamLeaderAnalytics } from "@/hooks/analytics/use-team-leader-analytics";
import type { TeamLeaderPerformance } from "@/lib/api/analytics";
import { useTLStatusCounts } from "@/hooks/use-tl-dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import {
  CategoryPieChart,
  type CategorySlice,
} from "@/components/analytics/charts/CategoryPieChart";
import { TrendingUp, AlertTriangle } from "lucide-react";

interface PerformanceMetricsContentProps {
  isLoading: boolean;
  isError: boolean;
  perf?: TeamLeaderPerformance;
  onRefetch: () => void;
}

function PerformanceMetricsContent({
  isLoading,
  isError,
  perf,
  onRefetch,
}: PerformanceMetricsContentProps) {
  if (isLoading) {
    return (
      <div className="space-y-4 py-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-12" />
            </div>
            <Skeleton className="h-2 w-full" />
          </div>
        ))}
        <div className="flex justify-between pt-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-10" />
        </div>
        <div className="flex justify-between">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-10" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6 text-center space-y-3">
        <div className="flex items-center justify-center gap-2 text-destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <span className="text-sm font-medium">Could not load performance metrics.</span>
        </div>
        <Button variant="outline" size="sm" onClick={onRefetch}>
          Try again
        </Button>
      </div>
    );
  }

  if (!perf) {
    return (
      <EmptyState
        icon={TrendingUp}
        title="No performance data available"
        description="No performance records found for your account."
        className="py-6"
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Completion Rate */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Completion Rate</span>
          <span className="font-medium tabular-nums">{perf.completionRatePercent}%</span>
        </div>
        <Progress value={perf.completionRatePercent} />
      </div>

      {/* Exam Pass Rate */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Exam Pass Rate</span>
          <span className="font-medium tabular-nums">{perf.examPassRatePercent}%</span>
        </div>
        <Progress value={perf.examPassRatePercent} />
      </div>

      {/* Average Progress */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Average Progress</span>
          <span className="font-medium tabular-nums">{perf.averageProgressPercent}%</span>
        </div>
        <Progress value={perf.averageProgressPercent} />
      </div>

      {/* Learning Hours */}
      <div className="flex items-center justify-between text-sm pt-1">
        <span className="text-muted-foreground">Learning Hours</span>
        <span className="font-medium tabular-nums">{perf.learningHoursInWindow}h</span>
      </div>

      {/* Certificates Issued */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Certificates Issued</span>
        <span className="font-medium tabular-nums">{perf.certificateCount}</span>
      </div>
    </div>
  );
}

interface StatusDistributionContentProps {
  isLoading: boolean;
  isError: boolean;
  data: CategorySlice[];
  onRefetch: () => void;
}

function StatusDistributionContent({
  isLoading,
  isError,
  data,
  onRefetch,
}: StatusDistributionContentProps) {
  if (isLoading) {
    return <Skeleton className="aspect-square max-h-48 w-full rounded-lg" />;
  }

  if (isError) {
    return (
      <div className="p-4 text-center space-y-2 border rounded-lg bg-muted/10">
        <div className="flex items-center justify-center gap-2 text-destructive">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <span className="text-xs font-medium">Could not load training status distribution.</span>
        </div>
        <Button variant="outline" size="sm" onClick={onRefetch}>
          Try again
        </Button>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="py-6 text-center text-xs text-muted-foreground">
        No training status data available
      </div>
    );
  }

  return <CategoryPieChart data={data} className="aspect-square max-h-48 w-full" />;
}

export function PerformanceCard() {
  const { user } = useAuth();

  const {
    data: analyticsData,
    isLoading: analyticsLoading,
    isError: analyticsError,
    refetch: refetchAnalytics,
  } = useTeamLeaderAnalytics({});

  const {
    data: statusCounts,
    isLoading: statusCountsLoading,
    isError: statusCountsError,
    refetch: refetchStatusCounts,
  } = useTLStatusCounts();

  // Select the caller's own row by ID — never fall back to leaders[0]
  const myPerf = analyticsData?.leaders?.find((l) => l.teamLeaderId === user?.id);

  const trainingStatusData: CategorySlice[] = Object.entries(statusCounts?.trainingStatus || {})
    .filter(([_, value]) => typeof value === "number" && value > 0)
    .map(([name, value]) => ({ name, value }));

  return (
    <Card className="lg:col-span-1 flex flex-col justify-between">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" aria-hidden="true" />
          My Performance
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 flex-1 flex flex-col justify-between">
        <div>
          <PerformanceMetricsContent
            isLoading={analyticsLoading}
            isError={analyticsError}
            perf={myPerf}
            onRefetch={() => refetchAnalytics()}
          />
        </div>

        <div className="border-t pt-4">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Training Status Distribution
          </p>
          <StatusDistributionContent
            isLoading={statusCountsLoading}
            isError={statusCountsError}
            data={trainingStatusData}
            onRefetch={() => refetchStatusCounts()}
          />
        </div>
      </CardContent>
    </Card>
  );
}
